import {
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSList,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BillingFeeItemType,
  Currency,
  Invoice,
  InvoiceStatus,
  LineItem,
  LineItemType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useHistory } from "react-router-dom";
import { AlertEstimatedTaxWithholdings } from "../../../modules/TaxWithholdings/components/AlertEstimatedTaxWithholdings";
import { AlertTaxWithholdingsEnabled } from "../../../modules/TaxWithholdings/components/AlertTaxWithholdingsEnabled";
import { WSQueries } from "../../../query/WSQuery";
import {
  queriesDeductions,
  queriesInternalAccounts,
  queriesTaxWithholdings
} from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorActiveTaxWithholdingAccount } from "../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { selectorPayoutSettingsInstantFee } from "@selectors/selectorPayoutSettingsInstantFee";
import {
  FALLBACK_INSTANT_PAYOUT_FEE_PERCENTAGE,
  instantPayoutService
} from "../../../shared/services/instantPayout";
import { selectorIsInvoicePaidOffPlatform } from "../../selectors/selectorIsInvoicePaidOffPlatform";
import { selectorTaxRateLabel } from "../../../shared/selectors/selectorTaxRateLabel";
import { Totals, TotalsSection, TotalsSectionItem } from "../Totals/Totals";
import { getInvoiceTaxWithholdings } from "./getInvoiceTaxWithholdings";
import { getPayoutMethodName, usePayoutMethods } from "../../utils/payout";
import { payoutToTotals } from "./payoutToTotals";

const lineItemRegularPrice = (lineItem: LineItem) =>
  lineItem.unit
    ? (lineItem.unitCost || 0) * (lineItem.quantity || 0)
    : lineItem.totalCost || 0;

const lineItemDiscount = (lineItem: LineItem) => {
  if (lineItem.discount?.amount) {
    return lineItem.discount.amount;
  }

  if (lineItem.discount?.percentage) {
    return (
      (lineItemRegularPrice(lineItem) * lineItem.discount.percentage) / 100
    );
  }

  return 0;
};

const getLineItemsTotalsSection = (
  lineItems: LineItem[],
  currency: Currency,
  params?: { is1099Flow?: boolean }
): TotalsSection => {
  const items: TotalsSectionItem[] = [];

  lineItems.forEach(lineItem => {
    items.push([
      <>
        <WSFlexBox.CenterY wrap="nowrap">
          <WSText color="gray600">{lineItem.description}</WSText>
          {lineItem.lineItemType === LineItemType.REIMBURSEMENT ? (
            <WSPill py="NONE" ml="M" text="Reimbursable" />
          ) : null}
          {params?.is1099Flow && lineItem.metadata?.taxForm1099 ? (
            <WSPill theme="blue" ml="M" text="Included in 1099" />
          ) : null}
        </WSFlexBox.CenterY>

        {lineItem.unit && (
          <WSText.ParagraphSm color="gray500">
            {lineItem.quantity} {lineItem.unit}(s) @{" "}
            {formatMoney(lineItem.unitCost || 0, currency)}/{lineItem.unit}
          </WSText.ParagraphSm>
        )}
        {lineItem.detail && (
          <WSText.ParagraphSm color="gray500">
            {lineItem.detail}
          </WSText.ParagraphSm>
        )}
      </>,
      formatMoney(lineItemRegularPrice(lineItem), currency)
    ]);

    const discount = lineItemDiscount(lineItem);

    if (discount) {
      items.push([
        <WSText key="left" color="gray600">
          {lineItem.discount?.description}
          {lineItem.discount?.percentage
            ? ` (${lineItem.discount.percentage}%)`
            : ""}
        </WSText>,
        formatMoney(-discount, currency)
      ]);
    }
  });

  return { items };
};

const sumFees = (invoice: Invoice, type: BillingFeeItemType) =>
  (invoice.fees?.items || [])
    .filter(feeItem => feeItem.type === type)
    .reduce((total, feeItem) => total + feeItem.amount, 0);

const findFee = (invoice: Invoice, type: BillingFeeItemType) =>
  (invoice.fees?.items || []).find(feeItem => feeItem.type === type);

type Props = {
  invoice: Invoice;
  is1099Flow?: boolean;
} & WSElementProps;

export const InvoiceTotals: React.FC<Props> = ({
  invoice,
  is1099Flow,
  ...elementProps
}) => {
  const history = useHistory();
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const querySettings =
    queriesTaxWithholdings.getTaxWithholdingSettings.useQuery();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryDeductions = queriesDeductions.listDeductions.useQuery(
    { filter: { payeeId: { eq: invoice.payeeId } } },
    { enabled: (invoice.deductionIds || []).length > 0 }
  );

  const hasActiveTaxWithholdingAccount =
    queryInternalAccounts.data?.data?.some(
      selectorActiveTaxWithholdingAccount
    ) ?? false;

  const { payoutPreferences, payoutMethods } = usePayoutMethods();

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          queryPayoutSettings
        }}
      >
        {({ queryPayoutSettings: { data: payoutSettings } }) => {
          const currency = invoice.currency ?? Currency.USD;
          const instantPayoutDefaultFee =
            selectorPayoutSettingsInstantFee(payoutSettings) ??
            FALLBACK_INSTANT_PAYOUT_FEE_PERCENTAGE;

          // An off-platform payment carries PAID_OFF_PLATFORM rather than PAID,
          // so without this the total reads "Pending" directly above the
          // invoice's own "Payment completed" line.
          const totalElement =
            invoice.status !== InvoiceStatus.PAID &&
            invoice.status !== InvoiceStatus.PAYMENT_IN_TRANSIT &&
            !selectorIsInvoicePaidOffPlatform(invoice) ? (
              <WSFlexBox.CenterY>
                <WSText mr="M">Total</WSText>
                <WSPill icon="time" text="Pending" />
              </WSFlexBox.CenterY>
            ) : (
              "Total"
            );

          const totalsSections: TotalsSection[] = [];

          // Line items

          const lineItems = invoice.lineItems || [];
          const lateFee = sumFees(invoice, BillingFeeItemType.LATE);
          const lineItemsAmount = lineItems.reduce(
            (total, lineItem) =>
              total +
              lineItemRegularPrice(lineItem) -
              lineItemDiscount(lineItem),
            0
          );
          const lineItemsSum = lineItemsAmount + lateFee;

          const totalsLineItemsSection = getLineItemsTotalsSection(
            lineItems,
            currency,
            { is1099Flow }
          );

          if (lateFee > 0) {
            totalsLineItemsSection.items.push([
              "Late fee",
              formatMoney(lateFee, currency)
            ]);
          }

          totalsSections.push(totalsLineItemsSection);

          // Fees & collaborator payments

          const creditCardFee =
            invoice.creditFeeHandling?.payeeFeePercentage === 100
              ? sumFees(invoice, BillingFeeItemType.CARD)
              : 0;

          const instantPayoutFeeItem = findFee(
            invoice,
            BillingFeeItemType.INSTANT_PAYOUT
          );
          const instantPayoutFee = instantPayoutFeeItem?.amount
            ? instantPayoutFeeItem.amount
            : invoice.status !== InvoiceStatus.PAID &&
                payoutPreferences === PreferredPayoutSpeed.INSTANT
              ? instantPayoutService.calculateInvoiceInstantPayoutFee(
                  lineItemsSum,
                  payoutSettings
                )
              : 0;

          const splitPayments = (invoice.splits || []).reduce(
            (total, split) => total + split.amount,
            0
          );

          const grossIncome =
            lineItemsSum - creditCardFee - instantPayoutFee - splitPayments;

          if (creditCardFee > 0 || instantPayoutFee > 0 || splitPayments > 0) {
            const totalsFeesSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: totalElement,
                  right: formatMoney(lineItemsSum, currency)
                }
              ]
            };

            if (creditCardFee) {
              totalsFeesSection.items.push([
                "Credit card fee",
                formatMoney(-creditCardFee, currency)
              ]);
            }

            if (instantPayoutFee) {
              if (instantPayoutFeeItem?.amount) {
                totalsFeesSection.items.push([
                  `Instant payout fee (${
                    (instantPayoutFeeItem.percentage || 0) * 100
                  }%)`,
                  formatMoney(-instantPayoutFeeItem.amount, currency)
                ]);
              } else {
                totalsFeesSection.items.push([
                  `Instant payout fee (${instantPayoutDefaultFee}%)`,
                  formatMoney(-instantPayoutFee, currency)
                ]);
              }
            }

            if (splitPayments) {
              totalsFeesSection.items.push([
                "Payment to contractors",
                formatMoney(-splitPayments, currency)
              ]);
            }

            totalsSections.push(totalsFeesSection);
          }

          let netIncome = grossIncome;

          // Tax withholdings & deductions
          const { amount: taxWithholdingAmount, rate: taxWithholdingRate } =
            getInvoiceTaxWithholdings(
              querySettings.data,
              invoice,
              netIncome,
              hasActiveTaxWithholdingAccount
            );
          netIncome -= taxWithholdingAmount;

          const deductionIds = invoice.deductionIds || [];
          const deductions = (queryDeductions.data?.data || []).filter(
            deduction => deduction.id && deductionIds.includes(deduction.id)
          );
          deductions.forEach(deduction => {
            netIncome -= deduction.amount || 0;
          });

          if (deductions.length > 0 || taxWithholdingAmount > 0) {
            const section: TotalsSection = {
              items: [
                {
                  bold: true,
                  left:
                    grossIncome === lineItemsSum
                      ? totalElement
                      : "Gross income",
                  right: formatMoney(grossIncome, currency)
                },
                ...deductions.map(deduction => ({
                  left: deduction.name,
                  right: formatMoney(-(deduction.amount || 0), currency)
                }))
              ]
            };

            if (taxWithholdingAmount > 0) {
              section.items.push({
                left: `Tax withholdings (${selectorTaxRateLabel(
                  taxWithholdingRate
                )})`,
                right: formatMoney(-taxWithholdingAmount, currency)
              });
            }

            totalsSections.push(section);
          }

          if (invoice.events?.markedPaidAt) {
            // Recorded total

            totalsSections.push({
              items: [
                {
                  bold: true,
                  left: "Recorded total",
                  right: formatMoney(netIncome, currency)
                }
              ]
            });
          } else {
            // Payout destinations

            const payoutDestinationsSection: TotalsSection = {
              items: [
                {
                  bold: true,
                  left: netIncome === grossIncome ? totalElement : "Net income",
                  right: formatMoney(netIncome, currency)
                }
              ]
            };

            const payouts = invoice.payouts || [];

            if (payouts.length > 0) {
              payouts
                .filter(payout => Boolean(payout.amount))
                .forEach(payout => {
                  payoutDestinationsSection.items.push(
                    payoutToTotals(payout, currency, history)
                  );
                });
            } else {
              if (payoutPreferences === PreferredPayoutSpeed.INSTANT) {
                payoutMethods.instant.active.forEach(payoutMethod => {
                  payoutDestinationsSection.items.push([
                    getPayoutMethodName(payoutMethod),
                    formatMoney(
                      (netIncome * (payoutMethod.percentage || 0)) / 100,
                      currency
                    )
                  ]);
                });
              } else {
                payoutMethods.standard.active.forEach(payoutMethod => {
                  payoutDestinationsSection.items.push([
                    getPayoutMethodName(payoutMethod),
                    formatMoney(
                      (netIncome * (payoutMethod.percentage || 0)) / 100,
                      currency
                    )
                  ]);
                });
              }
            }

            totalsSections.push(payoutDestinationsSection);
          }

          return (
            <WSList gap="XL">
              <Totals sections={totalsSections} />

              {!invoice.events?.markedPaidAt && (
                <>
                  <AlertEstimatedTaxWithholdings
                    invoiceNetAmount={netIncome}
                    invoiceCurrency={currency}
                  />
                  <AlertTaxWithholdingsEnabled />
                </>
              )}
            </WSList>
          );
        }}
      </WSQueries>
    </WSElement>
  );
};
